import React from "react";

import "rc-slider/assets/index.css";
import { useTranslation } from "react-i18next";

import ContentCollectionComponent from "./ContentCollectionComponent";
import ContentSimilarComponent from "./ContentSimilarComponent";
import ContentTrailerAutoplayComponent from "./ContentTrailerAutoplayComponent";
import ContentWidgetComponent from "./ContentWidgetComponent";
import { COMPONENT_TYPES } from "../../../../constants/componentType";
import {
  ADDITIONNAL_IMAGE_FORMATS,
  DEFAULT_IMAGE_FORMATS,
  IMAGE_FORMATS,
} from "../../../../constants/ratio";
import {
  getComponentByType,
  getComponentByTypeOrName,
} from "../../../../utils/data/Architecture";
import ImageFormatSelector from "../ImageFormatSelector";
import ShareConfig from "../section/ShareConfig";

import type { JSX } from "react";

import type {
  CarouselComponent,
  ContentPageImageFormatComponent,
  ContentPagePlatformComponent,
  ContentPageTrailerParams,
  ShareComponent,
  WidgetComponent as WidgetComponentType,
} from "../../../../api/ProjectAPI.types";
import type { ComponentType } from "../../../../constants/componentType";
import type { ImageFormat } from "../../../../constants/ratio";

interface ContentPageEditorProps {
  components: ContentPagePlatformComponent[];
  onChangeComponents: (
    updatedComponents: ContentPagePlatformComponent[],
    disableDataChange?: boolean,
  ) => void;
}

function ContentPageEditor({
  components,
  onChangeComponents,
}: ContentPageEditorProps): JSX.Element | null {
  const { SHARE, WIDGET, CAROUSEL, IMAGE_FORMAT } = COMPONENT_TYPES;
  const { FORMAT_16_6 } = IMAGE_FORMATS;

  const { t } = useTranslation("editor-platform");

  const handleChangeOption = ({
    key,
    value,
    type,
    staticComponentName,
  }: {
    key:
      | keyof ShareComponent["params"]
      | keyof WidgetComponentType["params"]
      | keyof CarouselComponent["params"]
      | keyof ContentPageImageFormatComponent["params"];
    value: string | number | boolean | null | ContentPageTrailerParams;
    type: ComponentType;
    staticComponentName?: string;
  }): void => {
    const currentComponent = getComponentByTypeOrName({
      components,
      type,
      name: staticComponentName,
    });

    const updatedComponents = components.map((component) => {
      if (component.uuid === currentComponent?.uuid) {
        return {
          ...component,
          params: {
            ...component.params,
            [key]: value,
          },
        };
      }

      return component;
    });

    onChangeComponents(updatedComponents as ContentPagePlatformComponent[]);
  };

  const handleChangeEnabled = ({
    value,
    type,
    staticComponentName,
  }: {
    value: boolean;
    type: string;
    staticComponentName?: string;
  }): void => {
    const currentComponent = getComponentByTypeOrName({
      components,
      type,
      name: staticComponentName,
    });

    const updatedComponents = components.map((component) => {
      if (component.uuid === currentComponent?.uuid) {
        return {
          ...component,
          enabled: value,
        };
      }

      return component;
    });

    onChangeComponents(updatedComponents);
  };

  const carouselSimilarContentComponent = getComponentByTypeOrName({
    components,
    type: CAROUSEL,
    name: "similar_contents",
  });
  const shareComponent = getComponentByType(components, SHARE);
  const widgetComponent = getComponentByType(components, WIDGET);
  const carouselCollectionsComponent = getComponentByTypeOrName({
    components,
    type: CAROUSEL,
    name: "collections_contents",
  });
  const imageFormatComponent: ContentPageImageFormatComponent | undefined =
    getComponentByType(components, IMAGE_FORMAT);
  const imageFormatSelected = imageFormatComponent
    ? imageFormatComponent.params?.ratio || FORMAT_16_6
    : undefined;
  const trailer = imageFormatComponent?.params.trailer;

  const handleChangeTrailer = (
    patch: Partial<ContentPageTrailerParams>,
  ): void => {
    handleChangeOption({
      type: IMAGE_FORMAT,
      key: "trailer",
      value: { autoplay: false, autoplay_delay: null, ...trailer, ...patch },
    });
  };

  if (
    !carouselSimilarContentComponent ||
    !shareComponent ||
    !widgetComponent ||
    !carouselCollectionsComponent
  ) {
    return null;
  }

  return (
    <React.Fragment>
      <div className="editor-tile-container">
        <div className="pt-[0.625rem]">
          <ImageFormatSelector
            selected={imageFormatSelected}
            title={t("pictureRatioContent")}
            imageFormatList={[
              ...DEFAULT_IMAGE_FORMATS,
              ...ADDITIONNAL_IMAGE_FORMATS,
            ]}
            onChangeRatio={(ratio: ImageFormat): void =>
              handleChangeOption({
                type: IMAGE_FORMAT,
                key: "ratio",
                value: ratio,
              })
            }
          />
        </div>
        {imageFormatComponent ? (
          <ContentTrailerAutoplayComponent
            delaySeconds={trailer?.autoplay_delay ?? null}
            isAutoplayed={trailer?.autoplay ?? false}
            onChangeAutoplay={(autoplay: boolean): void =>
              handleChangeTrailer({ autoplay })
            }
            onChangeDelay={(autoplay_delay: number | null): void =>
              handleChangeTrailer({ autoplay_delay })
            }
          />
        ) : null}
      </div>
      <div className="editor-form-container editor-tile-container">
        <ShareConfig
          shareComponent={shareComponent}
          onChangeOpt={(
            key: keyof ShareComponent["params"],
            value: boolean,
          ): void => handleChangeOption({ type: SHARE, key, value })}
        />
        <ContentSimilarComponent
          component={carouselSimilarContentComponent}
          onChangeEnabled={handleChangeEnabled}
          onChangeOption={handleChangeOption}
        />
        <ContentCollectionComponent
          component={carouselCollectionsComponent}
          onChangeEnabled={handleChangeEnabled}
          onChangeOption={handleChangeOption}
        />
        <ContentWidgetComponent
          component={widgetComponent}
          onChangeEnabled={handleChangeEnabled}
          onChangeOption={handleChangeOption}
        />
      </div>
    </React.Fragment>
  );
}

export default ContentPageEditor;
